import React from 'react';
import {
  View, StyleSheet, Text, Pressable,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import Moment from 'moment';
import Colors from '../../constants/Colors';
import HighlightsEmptyImage from './HighlightsEmptyImage';
import DateLabel from '../Occupancy/DateLabel';
import Style from '../../constants/Style';
import DownArrow from '../DownArrow';
import PriorityLabel from '../../screens/LocationManagement/components/PriorityLabel';
import CircleChart from '../CircleChart';
import TargetBar from './TargetBar';
import { AppScreen } from '../../navigation/LinkingConfiguration';

const HighlightsSection = ({
  selectedIndex, ticketCounts, electricValue, electricTarget, waterValue, waterTarget, occupancy,
  bookings,
}) => {
  const navigation = useNavigation();

  const from = Moment().subtract(6, 'days').startOf('day').toDate()
    .toISOString();

  const to = Moment().endOf('day').toDate()
    .toISOString();

  const range = {
    start: from,
    end: to,
  };

  const getPercentage = (value) => {
    const total = Math.max((ticketCounts.low
      + ticketCounts.normal + ticketCounts.high + ticketCounts.critical), 1);
    return (value / total) * 100;
  };
  const Empty = () => <>
    <HighlightsEmptyImage />
    <View style={styles.textSection}>
      <Text style={styles.noInsightsTitle}>{'No data here yet'}</Text>
      <Text style={styles.noInsightsDescription}>
        {'Statistics to appear here when live'}
      </Text>
    </View>
  </>;

  const Flag = ({ index, count }) => <View style={[Style.row, { marginHorizontal: 8 }]}>
    <PriorityLabel priority={index} />
    <Text style={[styles.smallTitle, { marginLeft: 6 }]}>{count}</Text>
  </View>;

  const Exceed = ({ exceeds }) => <View style={[Style.row, exceeds ? styles.exceed : styles.meet]}>
    <Text style={[styles.widgetPercent, exceeds ? { color: Colors.light.hereworksRed700 } : {}]}>
      {exceeds ? 'Exceed' : 'Meet'} targets</Text>
  </View>;

  const Divider = () => <View
    style={{
      width: 1, backgroundColor: Colors.light.hereworksBlue50, top: 0, bottom: 0,
    }} />;

  const ValueWidget = ({
    title, value, percent, trendingUp = false, link = '', metricIndex = 0, graph = '', testID = undefined,
  }) => <Pressable style={(state) => [{
    marginHorizontal: 8,
    alignItems: 'center',
    flex: 1,
    borderRadius: 8,
    paddingVertical: 24,
    paddingHorizontal: 22,
  }, link && state.hovered && { backgroundColor: Colors.light.hereworksWhite200 }]}
    disabled={!link}
    {...{ testID }}
    onPress={() => {
      navigation.navigate(link as any, { metricIndex, graph });
    }}>
      <Text style={styles.widgetTitle}>{title}</Text>
      <Text style={styles.widgetValue}>{value}</Text>
      <View style={Style.row}>
        <DownArrow color={trendingUp ? Colors.light.hereworksRed500
          : Colors.light.hereworksGreen500}
          style={{ transform: [{ rotateX: trendingUp ? '180deg' : '0deg' }] }} />
        <Text style={styles.widgetPercent}>{percent}%</Text>
      </View>
    </Pressable>;

  const TargetWidget = ({
    title, value, exceeds = false, link = '', graph = '',
  }) => <Pressable style={(state) => [{
    marginHorizontal: 8,
    alignItems: 'center',
    flex: 1,
    borderRadius: 8,
    paddingVertical: 36,
  }, link && state.hovered && { backgroundColor: Colors.light.hereworksWhite200 }]}
    disabled={!link}
    onPress={() => {
      navigation.navigate(link as any, { graph });
    }}>
      <Text style={styles.widgetTitle}>{title}</Text>
      <Text style={styles.widgetValue}>{value}</Text>
      <Exceed exceeds={exceeds} />
    </Pressable>;

  const Date = () => <DateLabel range={range} style={{ marginBottom: 16 }} />;

  const sectionContent = () => {
    switch (selectedIndex) {
      case 0:
        return <>
          <Date />
          <View style={{ flexDirection: 'row' }}>
            <View style={[Style.row, { flex: 1 }]}>
              <View style={[styles.widgetContainer, { marginRight: 12 }]}>
                <Text style={styles.subtitle}>Building occupancy</Text>
                <ValueWidget title={''} value={occupancy} percent={100} trendingUp={false}
                  link='BuildingOccupancy' graph='occupancy'
                  testID='building-occupancy-button'
                />
              </View>
              <View style={[styles.widgetContainer, { marginRight: 12 }]}>
                <Text style={styles.subtitle}>Bookings</Text>
                <ValueWidget title={''} value={bookings} percent={100} trendingUp={false}
                  link='BuildingOccupancy' graph='bookings'
                  testID='bookings-button'
                />
              </View>
            </View>
            <View style={{ flex: 1 }}>
              <Pressable style={(state) => [styles.widgetContainer, state.hovered
                && { backgroundColor: Colors.light.hereworksWhite200 }]}
                onPress={() => {
                  navigation.navigate(AppScreen.TICKETS as any);
                }}
                testID='ticket-raised-button'
              >
                <Text style={styles.subtitle}>Tickets raised</Text>
                <View style={[Style.row, { flex: 1, alignItems: 'flex-end' }]}>
                  <View>
                    <Text style={styles.smallTitle}>Priority</Text>
                    <View style={[Style.row, { marginTop: 8 }]}>
                      <Flag index={0} count={ticketCounts.critical} />
                      <Flag index={1} count={ticketCounts.high} />
                      <Flag index={2} count={ticketCounts.normal} />
                      <Flag index={3} count={ticketCounts.low} />
                    </View>
                  </View>
                  <View style={{ flex: 1, position: 'absolute', right: 0 }}>
                    <CircleChart
                      other={0}
                      low={getPercentage(ticketCounts.low)}
                      normal={getPercentage(ticketCounts.normal)}
                      high={getPercentage(ticketCounts.high)}
                      urgent={getPercentage(ticketCounts.critical)}
                      compareOther={0}
                      compareLow={0}
                      compareNormal={0}
                      compareHigh={0}
                      compareUrgent={0}
                      compareToggled={false}
                      radius={60}
                      stroke={8} />
                  </View>
                  <Text style={[styles.widgetValue, {
                    position: 'absolute', width: 100, textAlign: 'center', right: 22, top: 20,
                  }]}>{ticketCounts.low + ticketCounts.normal
                    + ticketCounts.high + ticketCounts.critical}</Text>
                </View>
              </Pressable>
            </View>
          </View>
          <View style={{ marginTop: 12 }}>
            <Text style={styles.title}>Targets</Text>
            <DateLabel />
            <View style={Style.row}>
              <View style={[styles.dynamicWidgetContainer, { marginRight: 12 }]}>
                <Text style={styles.subtitle}>Electricity</Text>
                <View style={{ marginTop: 8, alignItems: 'flex-start' }}>
                  <Exceed exceeds={false} />
                </View>
                <TargetBar current={electricValue} target={electricTarget} />
              </View>
              <View style={styles.dynamicWidgetContainer}>
                <Text style={styles.subtitle}>Water</Text>
                <View style={{ marginTop: 8, alignItems: 'flex-start' }}>
                  <Exceed exceeds={true} />
                </View>
                <TargetBar current={waterValue} target={waterTarget} />
              </View>
            </View>
          </View>
        </>;
      case 1:
        return <>
          <DateLabel />
          <View style={styles.widgetContainer}>
            <Text style={[styles.subtitle, { marginBottom: 24 }]}>
              IAQI</Text>
            <View style={{ flexDirection: 'row' }}>
              <ValueWidget title={'CO2 (ppm)'} value={60} percent={100} link={'InternalEnvironment'} metricIndex={3} />
              <Divider />
              <ValueWidget title={'PM10 (µg/m3)'} value={60} percent={100} link={'InternalEnvironment'} metricIndex={6} />
              <Divider />
              <ValueWidget title={'PM2.5 (µg/m3)'} value={30} percent={100} link={'InternalEnvironment'} metricIndex={5} />
              <Divider />
              <ValueWidget title={'TVOC (ppb)'} value={60} percent={100} link={'InternalEnvironment'} metricIndex={4} />
              <Divider />
              <ValueWidget title={'Temperature (°C)'} value={30} percent={100} link={'InternalEnvironment'} metricIndex={0} />
              <Divider />
              <ValueWidget title={'Humidity (%)'} value={30} percent={100} link={'InternalEnvironment'} metricIndex={1} />
            </View>
          </View></>;
      case 2:
        return <>
          <DateLabel />
          <View style={styles.widgetContainer}>
            <Text style={[styles.subtitle, { marginBottom: 24 }]}>
              External environment parameters</Text>
            <View style={{ flexDirection: 'row' }}>
              <ValueWidget title={'Rain (%)'} value={60} percent={100} link={'ExternalEnvironment'} metricIndex={0} />
              <Divider />
              <ValueWidget title={'Weather / cloud cover (%)'} value={60} percent={100} link={'ExternalEnvironment'} metricIndex={1} />
              <Divider />
              <ValueWidget title={'Temperature (°C)'} value={30} percent={100} link={'ExternalEnvironment'} metricIndex={2} />
              <Divider />
              <ValueWidget title={'Wind speed (m/s)'} value={60} percent={100} link={'ExternalEnvironment'} metricIndex={3} />
              <Divider />
              <ValueWidget title={'Travel time (min)'} value={30} percent={100} link={'ExternalEnvironment'} metricIndex={4} />
            </View>
          </View></>;
      case 4:
        return <>
          <DateLabel />
          <View style={styles.widgetContainer}>
            <Text style={[styles.subtitle, { marginBottom: 24 }]}>
              Energy targets</Text>
            <View style={{ flexDirection: 'row' }}>
              <TargetWidget title={'Electricity (kWh)'} value={3500} link={'Energy'} graph={'electricity'} />
              <Divider />
              <TargetWidget title={'Gas (m3)'} value={60} exceeds link={'Energy'} graph={'gas'} />
              <Divider />
              <TargetWidget title={'CO2 (kg)'} value={30} link={'Energy'} graph={'co2'} />
              <Divider />
              <TargetWidget title={'Water (m3)'} value={60} link={'Energy'} graph={'water'} />
              <Divider />
              <TargetWidget title={'Waste (kg)'} value={30} link={'Energy'} graph={'waste'} />
            </View>
          </View></>;
      case 5:
        return <>
          <DateLabel />
          <View style={Style.row}>
            <View style={[styles.widgetContainer, { marginRight: 12 }]}>
              <Text style={[styles.subtitle, { marginBottom: 24 }]}>
                Building occupancy</Text>
              <ValueWidget title={''} value={0} percent={100}
                link={'BuildingOccupancy'} graph='occupancy' />
            </View>
            <View style={[styles.widgetContainer, { marginRight: 12 }]}>
              <Text style={[styles.subtitle, { marginBottom: 24 }]}>
                Bookings</Text>
              <ValueWidget title={''} value={0} percent={100}
                link={'BuildingOccupancy'} graph='bookings' />
            </View>
            <View style={[styles.widgetContainer, { flex: 2 }]}>
              <Text style={[styles.subtitle, { marginBottom: 24 }]}>
                Most utilised spaces</Text>
              <View style={Style.row}>
                <ValueWidget title={'Gym'} value={0} percent={100}
                  link={'BuildingOccupancy'} graph='mostutilised' />
                <Divider />
                <ValueWidget title={'Reception'} value={0} percent={100}
                  link={'BuildingOccupancy'} graph='mostutilised' />
                <Divider />
                <ValueWidget title={'Café'} value={0} percent={100}
                  link={'BuildingOccupancy'} graph='mostutilised' />
              </View>
            </View>
          </View></>;
      case 3:
      case 6:
      case 7:
      case 8:
      default:
        return <Empty />;
    }
  };

  return (<View style={styles.container}>
    <Text style={styles.title}>Overview</Text>
    <View style={styles.content}>
      <View style={{ flex: 1 }}>
        {sectionContent()}
      </View>
    </View>
  </View>);
};

const styles = StyleSheet.create({
  container: {
    marginTop: 40,
    marginHorizontal: 40,
  },
  title: {
    fontSize: 22,
    lineHeight: 33,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginBottom: 4,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  textSection: {
    marginStart: 40,
    alignItems: 'flex-start',
  },
  noInsightsTitle: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
    marginTop: 32,
    textAlign: 'center',
  },
  noInsightsDescription: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    textAlign: 'center',
  },
  widgetContainer: {
    flex: 1,
    alignSelf: 'stretch',
    backgroundColor: '#fff',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue50,
    padding: 16,
    marginTop: 16,
    minHeight: 185,
  },
  dynamicWidgetContainer: {
    flex: 1,
    alignSelf: 'stretch',
    backgroundColor: '#fff',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue50,
    padding: 16,
    marginTop: 16,
  },
  subtitle: {
    fontSize: 18,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginBottom: 8,
  },
  smallTitle: {
    fontSize: 16,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  widgetTitle: {
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    fontSize: 14,
    textAlign: 'center',
  },
  widgetValue: {
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    fontSize: 30,
    lineHeight: 30,
    marginVertical: 8,
  },
  widgetPercent: {
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksGreen500,
    fontSize: 14,
  },
  exceed: {
    borderColor: Colors.light.hereworksRed700,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginBottom: 16,
    borderRadius: 4,
    backgroundColor: Colors.light.hereworksRed50,
  },
  meet: {
    borderColor: Colors.light.hereworksGreen,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginBottom: 16,
    borderRadius: 4,
    backgroundColor: Colors.light.hereworksGreen50,
  },
});

export default HighlightsSection;
